import React, { FC, useCallback } from "react";

import AntDTable from "antd/es/table";
import cn from "classnames";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Team from "@mapmycustomers/shared/types/Team";

import Table from "@app/component/Table";
import DrillDownViewState from "@app/scene/dashboard/store/stackRank/DrillDownViewState";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";
import getLeaderboardFieldValue from "@app/util/leaderboard/getLeaderboardFieldValue";

import formatMetricValue from "../util/formatMetricValue";
import useColumns from "../util/useColumns";

import styles from "./DataTable.module.scss";

export interface DataTableProps {
  className?: string;
  data: LeaderboardItem[];
  drillDownViewState: DrillDownViewState;
  loading?: boolean;
  onTeamClick?: (teamId: Team["id"]) => void;
  rankingMetric: IField;
}

const DataTable: FC<DataTableProps> = ({
  className,
  data,
  drillDownViewState,
  loading,
  onTeamClick,
  rankingMetric,
}) => {
  const intl = useIntl();

  const tableColumns = useColumns(intl, drillDownViewState.columns, rankingMetric);

  const rowEventHandlers = useCallback(
    (item: LeaderboardItem) => ({
      onClick: () => {
        if (item.teamId) {
          onTeamClick?.(item.teamId);
        }
      },
    }),
    [onTeamClick]
  );

  const getSummaryRow = useCallback(
    (data: readonly LeaderboardItem[]) => {
      const visibleFieldsNames = new Set(
        drillDownViewState.columns.filter(({ visible }) => visible).map(({ field }) => field.name)
      );

      return (
        <AntDTable.Summary fixed>
          <AntDTable.Summary.Row>
            <AntDTable.Summary.Cell index={0} key={0} />
            <AntDTable.Summary.Cell index={1} key={1}>
              {intl.formatMessage({
                id: "dashboard.stackRankDrillDown.table.summary",
                defaultMessage: "Total",
                description: "Title of the summary row in stack rank drill down's table",
              })}
            </AntDTable.Summary.Cell>
            {leaderboardFieldModel.fields
              .filter(
                (field) =>
                  field.hasFeature(FieldFeature.METRIC) && visibleFieldsNames.has(field.name)
              )
              .sort((a, b) =>
                a.name === rankingMetric?.name ? -1 : b.name === rankingMetric?.name ? 1 : 0
              )
              .map((field, index) => {
                const metric = field.name as LeaderboardMetricFieldName;
                const sum = data.reduce((result, item) => {
                  return result + getLeaderboardFieldValue(metric, item);
                }, 0);
                return (
                  <AntDTable.Summary.Cell index={index + 2} key={field.name}>
                    {formatMetricValue(intl, field, sum)}
                  </AntDTable.Summary.Cell>
                );
              })}
          </AntDTable.Summary.Row>
        </AntDTable.Summary>
      );
    },
    [intl, drillDownViewState.columns, rankingMetric?.name]
  );

  return (
    <Table<LeaderboardItem>
      className={cn(styles.table, className)}
      clickableRows
      columns={tableColumns}
      dataSource={data}
      loading={loading}
      onRow={rowEventHandlers}
      pagination={false}
      rowKey={drillDownViewState.grouping === "team" ? "teamId" : "userId"}
      scrollable
      showBorder
      size="middle"
      summary={getSummaryRow}
    />
  );
};

export default DataTable;
